import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import axios from 'axios';
import {
  ArrowRight, Sparkles, CalendarDays, Coins, Layers, Brain, Clock,
  Instagram, Youtube, Twitter, Facebook, MessageCircle, Send, Smartphone, Mail,
  Check, X, Zap, Globe, Shield, BarChart3, Shuffle, Target, TrendingUp,
  Users, Building2, ChevronRight, ChevronDown, Star, Timer, Fingerprint
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const LANGUAGES = [
  { code: 'tr', label: 'TR', flag: '\ud83c\uddf9\ud83c\uddf7' },
  { code: 'en', label: 'EN', flag: '\ud83c\uddfa\ud83c\uddf8' },
  { code: 'de', label: 'DE', flag: '\ud83c\udde9\ud83c\uddea' },
  { code: 'fr', label: 'FR', flag: '\ud83c\uddeb\ud83c\uddf7' },
  { code: 'es', label: 'ES', flag: '\ud83c\uddea\ud83c\uddf8' },
  { code: 'ar', label: 'AR', flag: '\ud83c\uddf8\ud83c\udde6' },
];

export default function LandingPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [plans, setPlans] = useState({});
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [openPlan, setOpenPlan] = useState('pro');
  const lang = i18n.language?.split('-')[0] || 'tr';

  useEffect(() => {
    axios.get(`${API}/api/plans`).then(r => setPlans(r.data)).catch(() => {});
  }, []);

  const channels = [
    { name: 'Instagram', icon: Instagram, color: '#E1306C' },
    { name: 'YouTube', icon: Youtube, color: '#FF0033' },
    { name: 'X', icon: Twitter, color: '#A8B3BD' },
    { name: 'Facebook', icon: Facebook, color: '#1877F2' },
    { name: 'WhatsApp', icon: MessageCircle, color: '#22C55E' },
    { name: 'Telegram', icon: Send, color: '#2AABEE' },
    { name: 'SMS', icon: Smartphone, color: '#84CC16' },
    { name: 'Email', icon: Mail, color: '#F59E0B' },
  ];

  const features = [
    { icon: Brain, color: '#D4AF37', key: 'aiContent' },
    { icon: CalendarDays, color: '#38BDF8', key: 'aiPlan' },
    { icon: Clock, color: '#FBBF24', key: 'bestTime' },
    { icon: BarChart3, color: '#34D399', key: 'prediction' },
  ];

  const planOrder = ['starter', 'pro', 'agency'];

  return (
    <div className="min-h-screen bg-[#07090C]">

      {/* ============================================ */}
      {/* MOBILE HEADER - Clean, minimal */}
      {/* ============================================ */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#07090C]/90 backdrop-blur-xl border-b border-white/[0.04]">
        <div className="flex items-center justify-between px-5 h-14">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#00B8A9] flex items-center justify-center">
              <span className="text-[10px] font-bold text-black">T</span>
            </div>
            <span className="text-base font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>TekCep</span>
          </div>
          <Select value={lang} onValueChange={(v) => i18n.changeLanguage(v)}>
            <SelectTrigger className="w-[60px] h-8 bg-transparent border-white/[0.08] text-[11px] px-2" data-testid="landing-lang-switcher">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0D1117] border-white/10">
              {LANGUAGES.map(l => (
                <SelectItem key={l.code} value={l.code}>
                  <span className="text-xs">{l.flag} {l.label}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </header>

      {/* ============================================ */}
      {/* DESKTOP HEADER */}
      {/* ============================================ */}
      <header className="hidden lg:block fixed top-0 left-0 right-0 z-50 border-b border-white/5 bg-[#07090C]/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#00B8A9] flex items-center justify-center font-bold text-black text-sm">T</div>
            <span className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>TekCep</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-[#A9B4C0]">
            <a href="#features" className="hover:text-white transition-colors">{t('nav.features')}</a>
            <a href="#pricing" className="hover:text-white transition-colors">{t('nav.pricing')}</a>
            <a href="/pitch" className="hover:text-white transition-colors">{t('nav.pitch')}</a>
          </div>
          <div className="flex items-center gap-3">
            <Select value={lang} onValueChange={(v) => i18n.changeLanguage(v)}>
              <SelectTrigger className="w-[90px] h-8 bg-transparent border-white/10 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0D1117] border-white/10">
                {LANGUAGES.map(l => (
                  <SelectItem key={l.code} value={l.code}>
                    <span className="flex items-center gap-1.5 text-xs"><span>{l.flag}</span>{l.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="ghost" onClick={() => navigate('/auth')} className="text-sm" data-testid="landing-login-btn">{t('nav.login')}</Button>
            <Button onClick={() => navigate('/auth')} className="gold-sheen bg-[#D4AF37] text-[#0D1117] text-sm font-semibold" data-testid="landing-signup-btn">{t('nav.signup')}</Button>
          </div>
        </div>
      </header>

      {/* ============================================ */}
      {/* MOBILE HERO – Premium, clean, single CTA */}
      {/* ============================================ */}
      <section className="lg:hidden relative pt-20 pb-12 px-6 overflow-hidden">
        {/* Background glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[400px] rounded-full bg-gradient-to-b from-[#D4AF37]/[0.06] via-[#00B8A9]/[0.04] to-transparent blur-3xl pointer-events-none" />
        
        <div className="relative z-10 pt-8">
          {/* Tagline badge */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#D4AF37]/20 bg-[#D4AF37]/[0.06]">
              <Zap size={11} className="text-[#D4AF37]" />
              <span className="text-[11px] text-[#D4AF37] font-medium tracking-wide">{t('landing.badge')}</span>
            </div>
          </div>

          {/* Main heading */}
          <h1 className="text-[28px] leading-[1.15] font-semibold tracking-tight text-center text-white mb-5" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {t('landing.hero.title1')}
            <span className="text-[#D4AF37]">{t('landing.hero.titleGold')}</span>
            {t('landing.hero.titleMid')}
            <span className="text-[#00B8A9]">{t('landing.hero.titleTeal')}</span>
            {t('landing.hero.titleEnd')}
          </h1>

          {/* Subtitle */}
          <p className="text-[15px] leading-relaxed text-[#8B95A5] text-center max-w-sm mx-auto mb-10">
            {t('landing.hero.subtitle')}
          </p>

          {/* Single CTA */}
          <div className="flex justify-center">
            <Button
              size="lg"
              onClick={() => navigate('/auth')}
              className="w-full max-w-xs h-14 rounded-2xl bg-gradient-to-r from-[#D4AF37] to-[#C5A028] text-[#0A0C10] font-semibold text-base shadow-[0_8px_32px_-8px_rgba(212,175,55,0.4)] hover:shadow-[0_12px_40px_-8px_rgba(212,175,55,0.5)] transition-shadow"
              data-testid="landing-hero-primary-cta"
            >
              {t('landing.hero.cta')}
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* DESKTOP HERO */}
      {/* ============================================ */}
      <section className="hidden lg:block relative pt-32 pb-20 overflow-hidden">
        <div className="hero-spotlight absolute inset-0" />
        <div className="noise-overlay absolute inset-0" />
        <div className="relative z-10 max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-2 gap-12 items-center">
            <div>
              <Badge variant="outline" className="mb-6 border-[#D4AF37]/30 text-[#D4AF37]">
                <Zap size={12} className="mr-1" /> {t('landing.badge')}
              </Badge>
              <h1 className="text-5xl lg:text-[3.5rem] font-semibold tracking-tight leading-[1.08] mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {t('landing.hero.title1')}<span className="text-[#D4AF37]">{t('landing.hero.titleGold')}</span>{t('landing.hero.titleMid')}<span className="text-[#00B8A9]">{t('landing.hero.titleTeal')}</span>{t('landing.hero.titleEnd')}
              </h1>
              <p className="text-lg text-[#A9B4C0] max-w-lg mb-4 leading-7">{t('landing.hero.subtitle')}</p>
              <p className="text-xs text-[#6B7280] mb-8">{t('landing.hero.setupNote')}</p>
              <div className="flex items-center gap-4">
                <Button size="lg" onClick={() => navigate('/auth')} className="gold-sheen bg-[#D4AF37] text-[#0A0C10] hover:brightness-105 px-8 text-base font-semibold shadow-[0_10px_30px_-12px_rgba(212,175,55,0.35)]" data-testid="landing-hero-primary-cta-desktop">
                  {t('landing.hero.cta')} <ArrowRight size={18} className="ml-2" />
                </Button>
                <Button variant="outline" size="lg" onClick={() => navigate('/pitch')} className="border-white/15 text-sm">
                  {t('landing.hero.pitchBtn')} <ChevronRight size={16} className="ml-1" />
                </Button>
              </div>
            </div>
            <div className="flex items-center justify-center">
              <div className="relative w-80 h-80">
                {channels.map((ch, i) => {
                  const angle = (i * 360) / channels.length;
                  const r = 120;
                  const x = Math.cos((angle * Math.PI) / 180) * r;
                  const y = Math.sin((angle * Math.PI) / 180) * r;
                  return (
                    <div key={ch.name} className="absolute w-14 h-14 rounded-xl border border-white/10 bg-[#0D1117]/80 flex items-center justify-center backdrop-blur animate-float"
                      style={{ left: `calc(50% + ${x}px - 28px)`, top: `calc(50% + ${y}px - 28px)`, animationDelay: `${i*0.3}s`, boxShadow: `0 0 20px ${ch.color}20` }}>
                      <ch.icon size={24} style={{ color: ch.color }} />
                    </div>
                  );
                })}
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-2xl bg-gradient-to-br from-[#D4AF37] to-[#00B8A9] flex items-center justify-center shadow-lg">
                  <span className="text-3xl font-bold text-black" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>T</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* MOBILE CHANNEL STRIP */}
      {/* ============================================ */}
      <section className="lg:hidden px-6 pb-10">
        <div className="flex flex-wrap justify-center gap-3">
          {channels.map((ch, i) => (
            <div key={i} className="w-10 h-10 rounded-xl border border-white/[0.06] bg-white/[0.02] flex items-center justify-center"
              style={{ boxShadow: `0 0 12px ${ch.color}10` }}>
              <ch.icon size={18} style={{ color: ch.color }} />
            </div>
          ))}
        </div>
      </section>

      {/* Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-[#D4AF37]/20 to-transparent" />

      {/* ============================================ */}
      {/* MOBILE FEATURES – Premium stacked cards */}
      {/* ============================================ */}
      <section className="lg:hidden py-12 px-6" id="features">
        <h2 className="text-xl font-semibold tracking-tight text-center mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          {t('landing.features.title')}
        </h2>
        <p className="text-[13px] text-[#6B7280] text-center mb-8">
          {t('landing.features.subtitle')}
        </p>

        <div className="space-y-4">
          {features.map((f, i) => (
            <div key={i} className="relative p-5 rounded-2xl border border-white/[0.06] bg-gradient-to-br from-white/[0.03] to-transparent overflow-hidden">
              {/* Subtle corner glow */}
              <div className="absolute top-0 right-0 w-24 h-24 rounded-full blur-3xl opacity-20 pointer-events-none" style={{ background: f.color }} />
              <div className="relative z-10">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${f.color}12` }}>
                  <f.icon size={22} style={{ color: f.color }} />
                </div>
                <h3 className="text-base font-semibold mb-1.5 text-white">{t(`landing.features.${f.key}.title`)}</h3>
                <p className="text-[13px] leading-relaxed text-[#8B95A5]">{t(`landing.features.${f.key}.desc`)}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============================================ */}
      {/* DESKTOP FEATURES */}
      {/* ============================================ */}
      <section className="hidden lg:block py-20" id="features-desktop">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4 border-[#00B8A9]/30 text-[#00B8A9]">
              <Brain size={12} className="mr-1" /> {t('landing.features.badge')}
            </Badge>
            <h2 className="text-3xl font-semibold tracking-tight mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('landing.features.title')}</h2>
            <p className="text-[#A9B4C0] max-w-lg mx-auto">{t('landing.features.subtitle')}</p>
          </div>
          <div className="grid grid-cols-3 gap-6">
            {[...features, { icon: Shuffle, color: '#00B8A9', key: 'variations' }, { icon: Layers, color: '#D4AF37', key: 'channels' }].map((f, i) => (
              <Card key={i} className="card-sheen rounded-xl border border-white/10 bg-[#0D1117]/80 hover:border-white/15 transition-colors">
                <CardContent className="p-6">
                  <f.icon size={28} className="mb-4" style={{ color: f.color }} />
                  <h3 className="text-lg font-semibold mb-2">{t(`landing.features.${f.key}.title`)}</h3>
                  <p className="text-sm text-[#A9B4C0] leading-6">{t(`landing.features.${f.key}.desc`)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Desktop channels */}
      <section className="hidden lg:block py-8">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-4 gap-3">
            {channels.map((ch, i) => (
              <div key={i} className="flex items-center gap-3 p-4 rounded-xl border border-white/10 bg-[#0D1117]/60 border-l-2 hover:bg-white/5 transition-colors" style={{ borderLeftColor: ch.color }}>
                <ch.icon size={22} style={{ color: ch.color }} />
                <span className="text-sm font-medium">{ch.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="h-px bg-gradient-to-r from-transparent via-[#D4AF37]/20 to-transparent" />

      {/* ============================================ */}
      {/* MOBILE PRICING – Accordion */}
      {/* ============================================ */}
      <section className="lg:hidden py-12 px-6" id="pricing">
        <h2 className="text-xl font-semibold tracking-tight text-center mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          {t('landing.pricing.title')}
        </h2>
        <p className="text-[13px] text-[#6B7280] text-center mb-6">
          {t('landing.pricing.subtitle')}
        </p>

        {/* Billing toggle */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-white/[0.04] border border-white/[0.06]">
            <button onClick={() => setBillingCycle('monthly')}
              className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors ${billingCycle === 'monthly' ? 'bg-white/10 text-white' : 'text-[#6B7280]'}`}>
              {t('landing.pricing.monthly')}
            </button>
            <button onClick={() => setBillingCycle('yearly')}
              className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${billingCycle === 'yearly' ? 'bg-white/10 text-white' : 'text-[#6B7280]'}`}>
              {t('landing.pricing.yearly')}
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#34D399]/20 text-[#34D399]">-17%</span>
            </button>
          </div>
        </div>

        {/* Accordion plans */}
        <div className="space-y-3">
          {planOrder.map(planKey => {
            const plan = plans[planKey];
            if (!plan) return null;
            const isOpen = openPlan === planKey;
            const price = billingCycle === 'monthly' ? plan.price_monthly : Math.round(plan.price_yearly / 12);
            const isPopular = plan.popular;
            const features_list = plan.display_features?.[lang] || plan.display_features?.en || [];
            const disabled = plan.disabled_features?.[lang] || plan.disabled_features?.en || [];

            return (
              <div key={planKey} className={`rounded-2xl border overflow-hidden transition-all duration-300 ${
                isPopular && isOpen ? 'border-[#D4AF37]/30 bg-gradient-to-b from-[#D4AF37]/[0.04] to-transparent' 
                : isOpen ? 'border-white/10 bg-white/[0.02]'
                : 'border-white/[0.06] bg-transparent'
              }`}>
                {/* Accordion header */}
                <button
                  onClick={() => setOpenPlan(isOpen ? null : planKey)}
                  className="w-full flex items-center justify-between p-5"
                  data-testid={`pricing-accordion-${planKey}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="text-left">
                      <div className="flex items-center gap-2">
                        <span className="text-base font-semibold text-white">{plan.name}</span>
                        {isPopular && (
                          <span className="text-[9px] px-2 py-0.5 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] font-medium">
                            {t('landing.pricing.popular')}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-[#6B7280]">{t(`landing.pricing.${planKey}.tagline`)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <span className="text-xl font-bold tabular-nums text-white">${price}</span>
                      <span className="text-[11px] text-[#6B7280]">/{t('landing.pricing.perMonth')}</span>
                    </div>
                    <ChevronDown size={16} className={`text-[#6B7280] transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                  </div>
                </button>

                {/* Accordion content */}
                {isOpen && (
                  <div className="px-5 pb-5 space-y-4">
                    {/* Credits */}
                    <div className="flex items-center gap-2">
                      <Coins size={14} className="text-[#D4AF37]" />
                      <span className="text-sm tabular-nums font-medium text-white">{plan.credits_monthly}</span>
                      <span className="text-xs text-[#6B7280]">{t('landing.pricing.creditsPerMonth')}</span>
                    </div>

                    {plan.credit_multiplier < 1 && (
                      <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#34D399]/10 border border-[#34D399]/15">
                        <span className="text-[11px] text-[#34D399] font-medium">{t('landing.pricing.aiCostPrefix')}{plan.credit_multiplier}{t('landing.pricing.aiCostSuffix')}</span>
                      </div>
                    )}

                    {/* Audience */}
                    <p className="text-[12px] text-[#00B8A9] italic">{t(`landing.pricing.${planKey}.audience`)}</p>

                    {/* Highlights */}
                    <div className="space-y-2">
                      {(t(`landing.pricing.${planKey}.highlights`, { returnObjects: true }) || []).map((h, hi) => (
                        <div key={hi} className="flex items-start gap-2">
                          <Check size={13} className="text-[#D4AF37] mt-0.5 shrink-0" />
                          <span className="text-[13px] text-white/90">{h}</span>
                        </div>
                      ))}
                    </div>

                    {/* Feature list */}
                    <div className="space-y-1.5 pt-1">
                      {features_list.map((f, fi) => (
                        <div key={fi} className="flex items-start gap-2">
                          <Check size={12} className="text-[#00B8A9]/60 mt-0.5 shrink-0" />
                          <span className="text-[12px] text-[#8B95A5]">{f}</span>
                        </div>
                      ))}
                      {disabled.map((f, fi) => (
                        <div key={`d-${fi}`} className="flex items-start gap-2 opacity-30">
                          <X size={12} className="text-[#6B7280] mt-0.5 shrink-0" />
                          <span className="text-[12px] text-[#6B7280] line-through">{f}</span>
                        </div>
                      ))}
                    </div>

                    <Button onClick={() => navigate('/auth')}
                      className={`w-full h-12 rounded-xl text-sm font-semibold ${
                        isPopular
                          ? 'bg-gradient-to-r from-[#D4AF37] to-[#C5A028] text-[#0A0C10] shadow-[0_4px_20px_-4px_rgba(212,175,55,0.3)]'
                          : 'bg-white/[0.06] text-white border border-white/[0.08] hover:bg-white/[0.1]'
                      }`}
                      data-testid={`pricing-${planKey}`}>
                      {t('landing.hero.cta')}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ============================================ */}
      {/* DESKTOP PRICING */}
      {/* ============================================ */}
      <section className="hidden lg:block py-20" id="pricing-desktop">
        <div className="max-w-5xl mx-auto px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-semibold tracking-tight mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('landing.pricing.title')}</h2>
            <p className="text-[#A9B4C0] mb-6">{t('landing.pricing.subtitle')}</p>
            <Tabs defaultValue="monthly" className="inline-flex" onValueChange={setBillingCycle}>
              <TabsList className="bg-[#121B28]">
                <TabsTrigger value="monthly">{t('landing.pricing.monthly')}</TabsTrigger>
                <TabsTrigger value="yearly">{t('landing.pricing.yearly')} <Badge className="ml-2 bg-[#34D399]/20 text-[#34D399] border-0 text-[9px]">-17%</Badge></TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="grid grid-cols-3 gap-6">
            {planOrder.map(planKey => {
              const plan = plans[planKey]; if (!plan) return null;
              const price = billingCycle === 'monthly' ? plan.price_monthly : Math.round(plan.price_yearly / 12);
              const isPopular = plan.popular;
              const fl = plan.display_features?.[lang] || plan.display_features?.en || [];
              const dl = plan.disabled_features?.[lang] || plan.disabled_features?.en || [];
              return (
                <Card key={planKey} className={`card-sheen rounded-xl border bg-[#0D1117]/80 ${isPopular ? 'border-[#D4AF37]/40 ring-1 ring-[#D4AF37]/20 scale-[1.02]' : 'border-white/10'}`}>
                  <CardContent className="p-6 space-y-5">
                    {isPopular && <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30"><TrendingUp size={10} className="mr-1" /> {t('landing.pricing.popular')}</Badge>}
                    {planKey === 'agency' && <Badge className="bg-[#00B8A9]/20 text-[#00B8A9] border-[#00B8A9]/30"><Building2 size={10} className="mr-1" /> {t('landing.pricing.agencyBadge')}</Badge>}
                    <div><h3 className="text-xl font-semibold">{plan.name}</h3><p className="text-xs text-[#6B7280] mt-1">{t(`landing.pricing.${planKey}.tagline`)}</p></div>
                    <div className="flex items-baseline gap-1"><span className="text-4xl font-bold tabular-nums">${price}</span><span className="text-sm text-[#6B7280]">/ {t('landing.pricing.perMonth')}</span></div>
                    <div className="flex items-center gap-2 text-sm"><Coins size={16} className="text-[#D4AF37]" /><span className="font-medium tabular-nums">{plan.credits_monthly}</span><span className="text-[#6B7280]">{t('landing.pricing.creditsPerMonth')}</span></div>
                    {plan.credit_multiplier < 1 && <div className="text-xs text-[#34D399] bg-[#34D399]/10 rounded-lg px-2 py-1 border border-[#34D399]/20 inline-block">{t('landing.pricing.aiCostPrefix')}{plan.credit_multiplier}{t('landing.pricing.aiCostSuffix')}</div>}
                    <p className="text-xs text-[#00B8A9] italic">{t(`landing.pricing.${planKey}.audience`)}</p>
                    <ul className="space-y-2">
                      {(t(`landing.pricing.${planKey}.highlights`, { returnObjects: true }) || []).map((h, hi) => (<li key={`h-${hi}`} className="flex items-start gap-2 text-sm"><Check size={14} className="text-[#D4AF37] mt-0.5 shrink-0" /> {h}</li>))}
                      {fl.map((f, fi) => (<li key={fi} className="flex items-start gap-2 text-sm text-[#A9B4C0]"><Check size={14} className="text-[#00B8A9] mt-0.5 shrink-0" /> {f}</li>))}
                      {dl.map((f, fi) => (<li key={`d-${fi}`} className="flex items-start gap-2 text-sm text-[#A9B4C0]/40 line-through"><X size={14} className="text-[#6B7280] mt-0.5 shrink-0" /> {f}</li>))}
                    </ul>
                    <Button onClick={() => navigate('/auth')} className={`w-full ${isPopular ? 'gold-sheen bg-[#D4AF37] text-[#0A0C10] font-semibold' : 'bg-[#121B28] border border-white/10'}`} data-testid={`pricing-desktop-${planKey}`}>{t('landing.hero.cta')}</Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* MOBILE TRUST BLOCK */}
      {/* ============================================ */}
      <section className="lg:hidden py-8 px-6">
        <div className="flex items-center justify-center gap-6">
          <div className="flex items-center gap-2">
            <Timer size={14} className="text-[#D4AF37]" />
            <span className="text-[12px] text-[#8B95A5]">5–10 dk</span>
          </div>
          <div className="w-px h-4 bg-white/10" />
          <div className="flex items-center gap-2">
            <Fingerprint size={14} className="text-[#00B8A9]" />
            <span className="text-[12px] text-[#8B95A5]">{lang === 'tr' ? 'Teknik bilgi gerekmez' : 'No tech skills needed'}</span>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* MOBILE BOTTOM CTA */}
      {/* ============================================ */}
      <section className="lg:hidden px-6 pb-8">
        <Button onClick={() => navigate('/auth')}
          className="w-full h-14 rounded-2xl bg-gradient-to-r from-[#D4AF37] to-[#C5A028] text-[#0A0C10] font-semibold text-base shadow-[0_8px_32px_-8px_rgba(212,175,55,0.4)]"
          data-testid="landing-bottom-cta">
          {t('landing.hero.cta')}
          <ArrowRight size={18} className="ml-2" />
        </Button>
      </section>

      {/* Desktop Agency CTA */}
      <section className="hidden lg:block py-12">
        <div className="max-w-4xl mx-auto px-8">
          <Card className="card-sheen rounded-2xl border border-[#00B8A9]/20 bg-[#00B8A9]/[0.04]">
            <CardContent className="p-8 flex items-center gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-3"><Building2 size={24} className="text-[#00B8A9]" /><h3 className="text-xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('landing.agencyCta.title')}</h3></div>
                <p className="text-sm text-[#A9B4C0] leading-6">{t('landing.agencyCta.desc')}</p>
              </div>
              <Button onClick={() => navigate('/auth')} className="bg-[#00B8A9] text-white whitespace-nowrap">{t('landing.hero.cta')} <ArrowRight size={16} className="ml-2" /></Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ============================================ */}
      {/* FOOTER */}
      {/* ============================================ */}
      <footer className="border-t border-white/[0.04] py-6">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded bg-gradient-to-br from-[#D4AF37] to-[#00B8A9] flex items-center justify-center font-bold text-black text-[8px]">T</div>
            <span className="text-xs font-medium text-[#6B7280]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>TekCep</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/pitch" className="text-[11px] text-[#6B7280] hover:text-white transition-colors hidden lg:block">Pitch</a>
            <p className="text-[11px] text-[#6B7280]">&copy; {new Date().getFullYear()} TekCep</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
